import { Button } from "@msuite/ui/button";
import { useLoading } from "@msuite/ui/hooks";
import { useTranslation } from "@msuite/ui/localization";
import { useAsyncModal } from "@msuite/ui/modal";
import { SettingsItem } from "@msuite/ui/settings";
import React from "react";
import { useNavigate } from "react-router";
import { Can } from "@/components/citadel";
import { client } from "@/hono";
import { DeleteProjectModal } from "@/modals/projects/delete-project-modal";
import type { ProjectPanelBaseProps } from "../../types";
import { CONFIRM_DELETE_PROJECT } from "./shared";

export function DeleteProject({ projectId }: ProjectPanelBaseProps) {
	/** Hooks */
	const { t } = useTranslation();
	const controller = useAsyncModal<string>();
	const deleteLoader = useLoading();
	const navigate = useNavigate();

	/** Functions */
	async function handleDeleteProject() {
		try {
			deleteLoader.start();
			const confirm = await controller.confirm();
			if (confirm !== CONFIRM_DELETE_PROJECT) {
				throw new Error("Project deletion not confirmed");
			}
			await client.projects[":projectId"].$delete({ param: { projectId } });
			navigate("/projects");
		} catch (error) {
			console.error("Failed to delete project:", error);
		} finally {
			deleteLoader.stop();
		}
	}

	/** Render */
	return (
		<React.Fragment>
			<SettingsItem
				title={t("projects.delete_project")}
				description={t("projects.delete_project_description")}
			>
				<Can
					action="delete"
					subject="Project"
				>
					<Button
						isLoading={controller.isPending || deleteLoader.isLoading}
						variant="danger-ghost"
						onClick={handleDeleteProject}
						data-cy="delete-project-button"
					>
						{t("projects.delete_project")}
					</Button>
				</Can>
			</SettingsItem>
			<DeleteProjectModal controller={controller} />
		</React.Fragment>
	);
}
